import {
  CustomPill,
  Icon,
  Link,
  LoadingSkeleton,
  TextButton,
  Tooltip,
} from '@joor/design-system'
import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { OrderStatus } from '__generated__/atlas-types'

import { formatDateRange } from 'utils/dates'
import { formatNameWithCode } from 'utils/formatters'
import { fromGlobalId } from 'utils/transformations/graphql'

import { getFormattedCurrency } from 'shop/formatters'
import {
  getDateFormat,
  isAccountTypeBrand as isAccountTypeBrandSelector,
} from 'store/currentUser/selectors'

import { EMPTY_CELL_CONTENT } from 'components/Core/Table/Cell'

import { OrderErrors } from '../../review.queries'
import { OrderData } from '../Overview/Overview'
import DetailValue from './components/DetailValue/DetailValue'
import RetailerInfo from './components/RetailerInfo/RetailerInfo'
import { usePriceTypeForm } from './header.hooks'
import * as UI from './header.ui'

const OrderTitleWrapper = styled(UI.DetailTitle)`
  display: flex;
  gap: 5px;
  align-items: center;
`

export const LinesheetsWrapper = styled.div`
  height: 47px;
  overflow-y: scroll;
`

const StyledTextButton = styled(TextButton)`
  margin-top: 10px;
  padding-right: 20px;
`

const StyledCustomPill = styled(CustomPill)`
  font-size: var(--size-font-small);
  line-height: 16px;
  letter-spacing: var(--letter-spacing-tighter);
  padding: 0 8px;
  border: none;
  min-width: 153px;
  height: 16px;
`

enum HeaderTestIds {
  OrderNumberItem = 'header-order-number-item',
  RetailerNameItem = 'header-retailer-name-item',
  PriceTypeItem = 'header-price-type-item',
  StatusItem = 'header-status-item',
  StatusDropdown = 'header-status-dropdown',
  LinesheetNameItem = 'header-linesheet-name-item',
  InternalOrderIdItem = 'header-internal-order-id-item',
  ReferenceNumberItem = 'header-reference-number-item',
}

const DetailLoading = () => <LoadingSkeleton width="50%" height={10} />

const ErrorPriceTypeDetailItem = ({ isFetching }: { isFetching?: boolean }) => (
  <>
    <UI.DetailTitle className={'error'}>
      Price Type
      <Icon type="semantic" iconName="error" plain iconSize="small" />
    </UI.DetailTitle>
    {isFetching ? (
      <DetailLoading />
    ) : (
      <DetailValue>
        <UI.ErrorField> – </UI.ErrorField>
      </DetailValue>
    )}
  </>
)

type Props = {
  order?: OrderData
  error?: OrderErrors
  openAssignRetailerModal?: () => void
  isFetching?: boolean
  onHandleSaveBtnClick: () => void
}

export const ReadonlyHeaderDetails = ({
  order,
  error,
  openAssignRetailerModal,
  isFetching,
  onHandleSaveBtnClick,
}: Props) => {
  const isAccountTypeBrand = useSelector(isAccountTypeBrandSelector)

  const dateFormat = useSelector(getDateFormat)

  const { priceType } = usePriceTypeForm({
    orderId: order?.id,
    orderRetailerId: order?.retailerProfile?.id,
    orderPriceType: order?.priceType,
    isBrand: isAccountTypeBrand,
  })

  const { newCollectionsEditAndCreate } = useFlags()

  const applyItemClassNameSuffix = (suffix: 'sm' | 'md' | 'lg') =>
    isAccountTypeBrand ? suffix : 'default'

  const isPriceTypeMissing =
    (order && !order?.priceType?.id) ||
    error === OrderErrors.ORDER_HAS_PARTIAL_ERROR

  const shouldShowInternalId = Boolean(
    order?.brand?.orderSettings?.displayInternalOrderId,
  )

  const getLinesheetDetailsLink = (linesheetId: string) => {
    return newCollectionsEditAndCreate
      ? `${window.location.origin}/ra/linesheet/${fromGlobalId(linesheetId).id}`
      : `${window.location.origin}/collections/view/${
          fromGlobalId(linesheetId).id || ''
        }?brandId=${fromGlobalId(order?.brand?.id).id}`
  }

  return (
    <UI.DetailsWrapper>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
        <UI.DetailItem
          className={`item-left item-${applyItemClassNameSuffix('sm')}`}
          data-testid={HeaderTestIds.OrderNumberItem}
        >
          <OrderTitleWrapper>Order #</OrderTitleWrapper>
          {isFetching && <DetailLoading />}
          {!isFetching && (
            <DetailValue testId={HeaderTestIds.ReferenceNumberItem}>
              {order?.referenceNumber}
            </DetailValue>
          )}
          {!!order && isAccountTypeBrand && shouldShowInternalId && (
            <DetailValue testId={HeaderTestIds.InternalOrderIdItem}>{`${
              fromGlobalId(order.webOrderLookupId).id
            } (Internal)`}</DetailValue>
          )}
        </UI.DetailItem>
        <UI.DetailItem
          className={`inner-item item-${applyItemClassNameSuffix('md')}`}
          data-testid={HeaderTestIds.RetailerNameItem}
        >
          <UI.DetailTitle
            className={order?.orphanRetailer ? 'orphan-retailer-title' : ''}
          >
            {isAccountTypeBrand ? 'Retailer' : 'Brand'}
            {order?.orphanRetailer && (
              <StyledCustomPill
                color="var(--color-primary-800)"
                bgColor="#FF6200"
              >
                Connection Pending
              </StyledCustomPill>
            )}
          </UI.DetailTitle>
          {isFetching && <DetailLoading />}
          {!isFetching && isAccountTypeBrand && (
            <DetailValue>
              <RetailerInfo
                order={order}
                openAssignRetailerModal={openAssignRetailerModal}
              />
            </DetailValue>
          )}{' '}
          {!isFetching && !isAccountTypeBrand && (
            <DetailValue>{order?.brand?.name ?? ''}</DetailValue>
          )}
        </UI.DetailItem>
        <UI.DetailItem
          className={`inner-item item-${applyItemClassNameSuffix('md')}`}
          data-testid={HeaderTestIds.LinesheetNameItem}
        >
          <UI.DetailTitle>Linesheet</UI.DetailTitle>
          {isFetching && <DetailLoading />}
          <LinesheetsWrapper>
            {!isFetching && !order?.collections?.edges.length ? (
              EMPTY_CELL_CONTENT
            ) : (
              <DetailValue>
                {order?.collections?.edges.map((edge, index) => (
                  <div key={`linesheet-${index}`}>
                    <Link
                      data-testid={`linesheet-name-${edge?.node?.id}-link`}
                      key={edge?.node?.id}
                      target="_blank"
                      href={getLinesheetDetailsLink(edge?.node?.id || '')}
                      className="notranslate"
                    >
                      {formatNameWithCode(edge?.node?.name, edge?.node?.code)}
                    </Link>
                  </div>
                ))}
              </DetailValue>
            )}
          </LinesheetsWrapper>
        </UI.DetailItem>
        <UI.DetailItem
          className={`inner-item item-${applyItemClassNameSuffix('md')}`}
          data-testid={HeaderTestIds.LinesheetNameItem}
        >
          <UI.DetailTitle>Shipping Delivery Window</UI.DetailTitle>
          {isFetching && <DetailLoading />}
          {!isFetching && order?.shipping?.deliveryWindow && (
            <DetailValue>
              {!!formatDateRange(
                order?.shipping?.deliveryWindow?.startDate,
                order?.shipping?.deliveryWindow?.completeDate,
                { dateFormat, utc: true },
              )
                ? formatDateRange(
                    order?.shipping?.deliveryWindow?.startDate,
                    order?.shipping?.deliveryWindow?.completeDate,
                    { dateFormat, utc: true },
                  )
                : '—'}
            </DetailValue>
          )}
        </UI.DetailItem>
        <UI.DetailItem
          className={`inner-item item-${applyItemClassNameSuffix('sm')}`}
          data-testid={HeaderTestIds.PriceTypeItem}
        >
          {!isFetching && isPriceTypeMissing ? (
            <ErrorPriceTypeDetailItem isFetching={isFetching} />
          ) : (
            <>
              <UI.DetailTitle>Price Type</UI.DetailTitle>
              {isFetching ? (
                <DetailLoading />
              ) : (
                <DetailValue>{getFormattedCurrency(priceType)}</DetailValue>
              )}
            </>
          )}
        </UI.DetailItem>
      </div>
      {order?.status &&
        isAccountTypeBrand &&
        (order?.isOrphan ? (
          <Tooltip
            content="You must assign a retailer to edit the header info"
            defaultPlacement={'left'}
          >
            <StyledTextButton onClick={onHandleSaveBtnClick} disabled={true}>
              Edit
            </StyledTextButton>
          </Tooltip>
        ) : (
          <StyledTextButton
            onClick={onHandleSaveBtnClick}
            disabled={order.status === OrderStatus.CANCELLED}
          >
            Edit
          </StyledTextButton>
        ))}
    </UI.DetailsWrapper>
  )
}
